import React, { useEffect, useMemo } from "react";
import { useNationalDogSurveyContext } from "../../NationalDogSurvey/NationalDogSurvey.context";

import {
	type CheckboxGroupProps,
	NDSCheckboxGroup as NDSCheckboxGroupComponent,
} from "./CheckboxGroup/CheckboxGroup";
import {
	NumberPicker as NumberPickerComponent,
	type NumberPickerProps,
} from "./NumberPicker/NumberPicker";
import {
	NDSRadioGroup as NDSRadioGroupComponent,
	type RadioGroupProps,
} from "./RadioGroup/RadioGroup";
import {
	SelectMulti as SelectMultiComponent,
	type SelectMultiProps,
} from "./Select/Multi/SelectMulti";
import {
	SelectSingleBreeds as SelectSingleBreedsComponent,
	SelectSingle as SelectSingleComponent,
	type SelectSingleProps,
} from "./Select/Single/SelectSingle";

import {
	NDSTextInput as NDSTextInputComponent,
	type TextInputProps,
} from "./TextInput/TextInput";

import {
	NDSEditableField as NDSEditableFieldComponent,
	type NDSEditableFieldProps,
} from "./EditableField/EditableField";
import {
	LikertScale as LikertScaleComponent,
	type LikertScaleProps,
} from "./LikertScale/LikertScale";

// A higher order component which checks if the field is hidden, if it's not it calls updateFieldsSeen from useNationalDogSurveyContext

interface WithFieldSeenProps {
	hidden?: boolean;
	name?: string;
	noTracking?: boolean;
	label?: string;
	groupLabel?: string | { text: string; hide: boolean };
	legend?: string;
	options?: NDS.FormOption[];
}
const withFieldSeen = <T extends WithFieldSeenProps>(
	Component: React.FC<Omit<T, "noTracking">>,
) => {
	// Memoize the wrapped component for better performance
	const MemoizedComponent = React.memo(Component);

	return (props: T) => {
		const { hidden, name, noTracking } = props;
		const { updateFieldsSeen, surveyState, screenState, currentDogName } =
			useNationalDogSurveyContext();

		useEffect(() => {
			if (noTracking) {
				return;
			}
			if (!hidden && name && updateFieldsSeen) {
				updateFieldsSeen(name);
			}
		}, [noTracking, hidden, name, updateFieldsSeen]);

		// Memoize the replacement values to avoid recalculation on every render
		const replacementValues = useMemo(() => {
			const dogNum = screenState.dogCount === 1 ? "dog" : "dogs";
			const dogName = currentDogName.split("*!*")[0] || "your dog";
			const firstName = surveyState.warmData?.firstName || "";

			return { dogNum, dogName, firstName };
		}, [screenState.dogCount, currentDogName, surveyState.warmData?.firstName]);

		// Helper function to replace dynamic text placeholders
		const replaceDynamicText = useMemo(() => {
			return (text?: string) => {
				if (!text) return text;

				return text
					.replace("[dogNum]", replacementValues.dogNum)
					.replace("[dogName]", replacementValues.dogName)
					.replace("[firstName]", replacementValues.firstName);
			};
		}, [replacementValues]);

		// Apply dynamic text replacements to all fields - memoized to prevent unnecessary recalculations
		const labelWithDynamicText = useMemo(
			() => replaceDynamicText(props.label),
			[props.label, replaceDynamicText],
		);

		const legendWithDynamicText = useMemo(
			() => replaceDynamicText(props.legend),
			[props.legend, replaceDynamicText],
		);

		const groupLabelWithDynamicText = useMemo(() => {
			if (!props.groupLabel) return undefined;

			return typeof props.groupLabel === "string"
				? replaceDynamicText(props.groupLabel)
				: {
						hide: props.groupLabel.hide,
						text: replaceDynamicText(props.groupLabel.text),
					};
		}, [props.groupLabel, replaceDynamicText]);

		const optionsWithDynamicText = useMemo(() => {
			if ("options" in props) {
				return props.options.map((option) => {
					if (typeof option.label === "string") {
						return {
							text: replaceDynamicText(option.text),
							label: replaceDynamicText(option.label),
							value: replaceDynamicText(option.value),
							imgSrc: option.imgSrc,
						};
					}
					return option;
				});
			}
			return undefined;
		}, [props, replaceDynamicText]);
		return (
			<MemoizedComponent
				{...props}
				label={labelWithDynamicText}
				groupLabel={groupLabelWithDynamicText}
				legend={legendWithDynamicText}
				hidden={hidden}
				name={name}
				noTracking={noTracking}
				options={optionsWithDynamicText}
			/>
		);
	};
};

export const NDSCheckboxGroup = withFieldSeen<
	CheckboxGroupProps & WithFieldSeenProps
>(NDSCheckboxGroupComponent);
export const NDSRadioGroup = withFieldSeen<
	RadioGroupProps & WithFieldSeenProps
>(NDSRadioGroupComponent);
export const NDSTextInput = withFieldSeen<TextInputProps & WithFieldSeenProps>(
	NDSTextInputComponent,
);
export const NumberPicker = withFieldSeen<
	NumberPickerProps & WithFieldSeenProps
>(NumberPickerComponent);
export const SelectSingle = withFieldSeen<
	SelectSingleProps & WithFieldSeenProps
>(SelectSingleComponent);
export const SelectMulti = withFieldSeen<SelectMultiProps & WithFieldSeenProps>(
	SelectMultiComponent,
);
export const SelectSingleBreeds = withFieldSeen<
	Omit<SelectSingleProps, "options"> & WithFieldSeenProps
>(SelectSingleBreedsComponent);
export const NDSEditableField = withFieldSeen<
	NDSEditableFieldProps & WithFieldSeenProps
>(NDSEditableFieldComponent);
export const LikertScale = withFieldSeen<LikertScaleProps & WithFieldSeenProps>(
	LikertScaleComponent,
);
